/**
 * Checkbox primitive wrapping Radix for accessibility.
 * Renders a 20x20 box with a check icon when checked, plus a label and optional hint.
 */

import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { forwardRef, useId, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root>,
  "children"
> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
};

export const Checkbox = forwardRef<
  React.ElementRef<typeof RadixCheckbox.Root>,
  CheckboxProps
>(
  (
    { label, hint, error, id, containerClassName, className, ...rest },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div className={cn("space-y-1", containerClassName)}>
        <div className="flex items-start gap-3">
          <RadixCheckbox.Root
            {...rest}
            ref={ref}
            id={inputId}
            aria-describedby={
              [hintId, errorId].filter(Boolean).join(" ") || undefined
            }
            className={cn(
              "mt-0.5 h-5 w-5 flex-shrink-0 rounded-sm border border-border-strong bg-surface-card transition-colors",
              "focus:outline-none focus-visible:shadow-focus",
              "data-[state=checked]:border-brand-500 data-[state=checked]:bg-brand-500",
              "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60",
              error && "border-danger-500",
              className,
            )}
          >
            <RadixCheckbox.Indicator className="flex items-center justify-center text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </RadixCheckbox.Indicator>
          </RadixCheckbox.Root>

          <label
            htmlFor={inputId}
            className="cursor-pointer select-none text-caption text-ink"
          >
            {label}
          </label>
        </div>

        {hint && !error ? (
          <p id={hintId} className="ml-8 text-caption text-ink-subtle">
            {hint}
          </p>
        ) : null}

        {error ? (
          <p
            id={errorId}
            className="ml-8 text-caption text-danger-600"
            role="alert"
          >
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
Checkbox.displayName = "Checkbox";
